<script>
const SUBMENU_OPEN_DELAY = 100;
const SUBMENU_CLOSE_DELAY = 200;

export default {
  name: 'AkContextMenu',
  props: {
    items: {
      type: Array,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    triggerIcon: {
      type: String,
      default: 'ga-ellipsis-vertical',
    },
    triggerClass: {
      type: String,
      default:
        'h-8 w-8 flex justify-center items-center bg-grey-50 text-grey-900 rounded-full hover:bg-grey-100 focus-visible:bg-grey-100',
    },
  },
  emits: ['select'],
  watch: {
    submenuQuery() {
      this.filterSubmenuItems();
    },
  },
  data() {
    return {
      showMenu: false,
      menuStyle: {},
      openSubmenuKey: null,
      submenuStyle: {},
      submenuQuery: '',
      submenuAllItems: [],
      submenuItems: [],
      submenuLoading: false,
      submenuCache: {},
    };
  },
  mounted() {
    this._menuClose = (e) => {
      if (
        this.showMenu &&
        !this.$refs.menuRoot?.contains(e.target) &&
        !this.$refs.menu?.contains(e.target) &&
        !this.$refs.submenu?.contains(e.target)
      ) {
        this.closeAll();
      }
    };
    document.addEventListener('click', this._menuClose, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this._menuClose, true);
    clearTimeout(this._submenuOpenTimer);
    clearTimeout(this._submenuCloseTimer);
  },
  methods: {
    toggleMenu() {
      if (this.disabled) return;
      if (this.showMenu) {
        this.closeAll();
        return;
      }
      const rect = this.$refs.trigger.getBoundingClientRect();
      // best-effort guess (below the trigger) to avoid a flash while the menu isn't mounted yet to measure
      this.menuStyle = {left: `${rect.left}px`, top: `${rect.bottom + 8}px`};
      this.showMenu = true;
      // showMenu must flip before this call so the DOM update it queues flushes before this callback runs
      // (both share Vue's resolved-promise microtask queue, in attach order) and $refs.menu is mounted.
      this.$nextTick(() => {
        const menuHeight = this.$refs.menu?.offsetHeight ?? 0;
        const overflowsBottom = rect.bottom + 8 + menuHeight > window.innerHeight;
        if (overflowsBottom) {
          this.menuStyle = {left: `${rect.left}px`, bottom: `${window.innerHeight - rect.top + 8}px`};
        }
      });
    },
    closeAll() {
      this.showMenu = false;
      this.openSubmenuKey = null;
    },
    select(item, itemEl) {
      if (item.disabled) return;
      if (item.submenu) {
        // hover opens it on desktop; click is the fallback for touch devices, where hover never fires
        if (this.openSubmenuKey === item.key) this.openSubmenuKey = null;
        else this.openSubmenu(item, itemEl);
        return;
      }
      this.closeAll();
      this.$emit('select', item.key);
    },
    scheduleOpenSubmenu(item, itemEl, isDisabled) {
      // entering a sibling item is never "moving toward" the previously open submenu, so drop it right away
      // (the grace delay in scheduleCloseSubmenu is only for the item -> submenu-panel diagonal move)
      if (this.openSubmenuKey && this.openSubmenuKey !== item.key && !isDisabled) {
        clearTimeout(this._submenuCloseTimer);
        this.openSubmenuKey = null;
      }
      if (item.disabled || !item.submenu) return;
      clearTimeout(this._submenuCloseTimer);
      clearTimeout(this._submenuOpenTimer);
      this._submenuOpenTimer = setTimeout(() => this.openSubmenu(item, itemEl), SUBMENU_OPEN_DELAY);
    },
    scheduleCloseSubmenu() {
      clearTimeout(this._submenuOpenTimer);
      clearTimeout(this._submenuCloseTimer);
      this._submenuCloseTimer = setTimeout(() => {
        this.openSubmenuKey = null;
      }, SUBMENU_CLOSE_DELAY);
    },
    cancelCloseSubmenu() {
      clearTimeout(this._submenuCloseTimer);
    },
    openSubmenu(item, itemEl) {
      this.openSubmenuKey = item.key;
      this.submenuQuery = '';
      const itemRect = itemEl.getBoundingClientRect();
      const menuRect = this.$refs.menu.getBoundingClientRect();
      // best-effort guess (to the right of the main panel) to avoid a flash before it's mounted to measure
      this.submenuStyle = {left: `${menuRect.right + 4}px`, top: `${itemRect.top}px`};
      if (this.submenuCache[item.key]) {
        this.submenuAllItems = this.refreshSelection(item, this.submenuCache[item.key]);
        this.filterSubmenuItems();
      } else {
        this.submenuAllItems = [];
        this.submenuItems = [];
        this.loadSubmenuItems(item);
      }
      this.$nextTick(() => {
        const submenuEl = this.$refs.submenu;
        if (!submenuEl) return;
        const style = {...this.submenuStyle};
        if (menuRect.right + 4 + submenuEl.offsetWidth > window.innerWidth) {
          style.left = `${menuRect.left - submenuEl.offsetWidth - 4}px`;
        }
        if (itemRect.top + submenuEl.offsetHeight > window.innerHeight) {
          delete style.top;
          style.bottom = `${window.innerHeight - itemRect.bottom}px`;
        }
        this.submenuStyle = style;
      });
    },
    refreshSelection(item, items) {
      // the cache holds fetched data (label/value), but "currently selected" reflects live state
      // (e.g. the thread's assigned employee) that can have changed since the fetch, so it's never cached
      if (!item.submenu.isSelected) return items;
      return items.map((child) => ({...child, selected: item.submenu.isSelected(child.value)}));
    },
    filterSubmenuItems() {
      const query = this.submenuQuery.trim().toLowerCase();
      this.submenuItems = query
        ? this.submenuAllItems.filter((child) => child.label.toLowerCase().includes(query))
        : this.submenuAllItems;
    },
    loadSubmenuItems(item) {
      this.submenuLoading = true;
      item.submenu
        .loadItems()
        .then((items) => {
          this.submenuCache[item.key] = items;
          if (this.openSubmenuKey !== item.key) return;
          this.submenuAllItems = this.refreshSelection(item, items);
          this.filterSubmenuItems();
        })
        .finally(() => {
          this.submenuLoading = false;
        });
    },
    selectSubmenuItem(item, child) {
      this.closeAll();
      this.$emit('select', item.key, child);
    },
  },
};
</script>

<template>
  <div class="relative" ref="menuRoot">
    <button ref="trigger" type="button" :disabled="disabled" :class="triggerClass" @click.prevent.stop="toggleMenu">
      <slot name="trigger">
        <span class="ga-icon text-lg" :class="triggerIcon" />
      </slot>
    </button>
    <Teleport to="body">
      <div
        v-if="showMenu"
        ref="menu"
        class="fixed bg-white rounded-xl shadow-[0_4px_16px_rgba(0,0,0,0.08)] border border-grey-100 min-w-[180px] z-50 p-2"
        :style="menuStyle">
        <button
          v-for="item in items"
          :key="item.key"
          type="button"
          :disabled="item.disabled"
          class="w-full text-left px-2.5 py-2 font-medium flex items-center gap-2 rounded-md leading-[18px]"
          :class="[
            item.disabled ? 'text-grey-300 cursor-not-allowed' : 'text-grey-900 hover:bg-grey-50 cursor-pointer',
            openSubmenuKey === item.key ? 'bg-grey-50' : '',
            item.selected ? '!bg-secondary-50' : '',
          ]"
          @mouseenter="scheduleOpenSubmenu(item, $event.currentTarget, item.disabled)"
          @mouseleave="scheduleCloseSubmenu"
          @click.prevent.stop="select(item, $event.currentTarget)">
          <span
            v-if="item.icon"
            class="ga-icon text-lg"
            :class="[item.icon, item.disabled ? 'text-grey-300' : 'text-grey-900']" />
          <span class="grow" :class="{'font-semibold': item.selected}">{{ item.label }}</span>
          <slot name="item-suffix" :item="item" />
          <span v-if="item.submenu && !item.disabled" class="ga-icon ga-chevron_right text-sm text-grey-300" />
        </button>
      </div>
    </Teleport>
    <Teleport to="body">
      <div
        v-if="openSubmenuKey"
        ref="submenu"
        class="fixed bg-white rounded-xl shadow-[0_4px_16px_rgba(0,0,0,0.08)] border border-grey-100 min-w-[220px] max-w-[260px] z-50 p-2"
        :style="submenuStyle"
        @mouseenter="cancelCloseSubmenu"
        @mouseleave="scheduleCloseSubmenu">
        <div class="relative mb-1">
          <input
            type="text"
            v-model="submenuQuery"
            :placeholder="items.find((i) => i.key === openSubmenuKey)?.submenu?.placeholder"
            class="w-full px-2.5 py-1.5 pr-7 text-sm border border-grey-100 rounded-md focus:outline-none focus:border-secondary"
            @click.stop />
          <button
            v-if="submenuQuery"
            type="button"
            class="absolute right-1.5 top-1/2 -translate-y-2.5 flex items-center justify-center h-5 w-5 text-grey-300 hover:text-grey-500"
            @click.prevent.stop="submenuQuery = ''">
            <span class="ga-icon ga-supprimer text-base" />
          </button>
        </div>
        <div class="max-h-[240px] overflow-y-auto">
          <button
            v-for="child in submenuItems"
            :key="child.value"
            type="button"
            v-tooltip.right="
              child.preview ? {content: child.preview, popperClass: 'ak-context-menu-tooltip'} : undefined
            "
            class="w-full text-left px-2.5 py-2 font-medium flex items-center gap-2 rounded-md leading-[18px] text-grey-900 hover:bg-grey-50 cursor-pointer"
            @click.prevent.stop="
              selectSubmenuItem(
                items.find((i) => i.key === openSubmenuKey),
                child,
              )
            ">
            <span class="grow truncate">{{ child.label }}</span>
            <span v-if="child.selected" class="ga-icon ga-check text-secondary text-sm shrink-0" />
          </button>
          <div v-if="!submenuLoading && !submenuItems.length" class="px-2.5 py-2 text-sm text-grey-500">
            <slot name="submenu-empty">Aucun résultat</slot>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style>
/* le tooltip FloatingVue est teleporté hors du composant, donc pas de scoped ici */
.ak-context-menu-tooltip .v-popper__inner {
  max-width: 260px;
  white-space: pre-line;
  word-break: break-word;
}
</style>
